import { useEffect, useState } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import * as Sentry from "@sentry/react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { ThemeColorProvider } from "@/components/ThemeColorProvider";
import { HelmetProvider } from "react-helmet-async";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Welcome from "./pages/Welcome";
import Pricing from "./pages/Pricing";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import About from "./pages/About";
import Features from "./pages/Features";
import { RealEstateCrm, AiCrm, SmallBusinessCrm } from "./pages/solutions";
import DashboardLayout from "./pages/DashboardLayout";
import FreshLeads from "./pages/FreshLeads";
import ColdLeads from "./pages/ColdLeads";
import Refresher from "./pages/Refresher";
import Products from "./pages/Products";
import LeadSetup from "./pages/LeadSetup";
import Profile from "./pages/Profile";
import JoinGroup from "./pages/JoinGroup";
import NotFound from "./pages/NotFound";
import ProtectedRoute from "./components/ProtectedRoute";
import PublicRoute from "./components/PublicRoute";
import GroupLayout from "./components/GroupLayout";
import { supabase } from "@/integrations/supabase/client";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { requestWithAuthRecovery } from "@/lib/authRequest";

const queryClient = new QueryClient();

const DashboardHomeRedirect = () => {
  const location = useLocation();
  const { user } = useAuth();
  const [target, setTarget] = useState<string | null>(null);
  const [resolutionError, setResolutionError] = useState(false);
  const [resolutionAttempt, setResolutionAttempt] = useState(0);
  const userId = user?.id ?? null;

  useEffect(() => {
    let active = true;

    // Once a destination has been resolved, never recompute it: token
    // refreshes on tab focus would otherwise re-run this and can fall back to
    // the personal workspace.
    if (target) return;

    const resolveTarget = async () => {
      const fallback = "/dashboard/personal/hot";
      const savedWorkspace = userId
        ? localStorage.getItem(`last_dashboard_workspace:${userId}`)
        : null;
      setResolutionError(false);

      if (
        location.pathname === "/dashboard" &&
        (savedWorkspace?.startsWith("/dashboard/groups/") || savedWorkspace?.startsWith("/dashboard/personal/"))
      ) {
        setTarget(savedWorkspace);
        return;
      }

      try {
        if (!userId) {
          if (active) setTarget(fallback);
          return;
        }

        const [groupsResult, profileResult] = await Promise.all([
          requestWithAuthRecovery(() => supabase
            .from("groups")
            .select("id, group_members!inner(user_id)")
            .eq("group_members.user_id", userId)
            .order("created_at", { ascending: false })),
          requestWithAuthRecovery(() => supabase
            .from("profiles")
            .select("group_order")
            .eq("user_id", userId)
            .maybeSingle()),
        ]);

        const { data: groups, error: groupsError } = groupsResult;
        const { data: profile } = profileResult;

        if (groupsError) throw groupsError;

        if (!groups || groups.length === 0) {
          if (active) setTarget(fallback);
          return;
        }

        const order = (profile?.group_order as string[] | null) || [];
        const sorted = [...groups].sort((a: any, b: any) => {
          const ia = order.indexOf(a.id);
          const ib = order.indexOf(b.id);
          if (ia !== -1 && ib !== -1) return ia - ib;
          if (ia !== -1) return -1;
          if (ib !== -1) return 1;
          return 0;
        });

        if (active) setTarget(`/dashboard/groups/${(sorted[0] as any).id}/hot`);
      } catch (error) {
        console.warn("Dashboard workspace could not be resolved:", error);
        // Do not silently redirect to an empty personal sheet when group
        // discovery is temporarily unavailable. Keep the user on the loading
        // boundary and retry there instead.
        if (active) setResolutionError(true);
      }
    };

    resolveTarget();

    return () => {
      active = false;
    };
  }, [location.pathname, userId, target, resolutionAttempt]);

  if (!target) {
    if (resolutionError) {
      return (
        <div className="p-8 text-sm text-muted-foreground">
          <p>We couldn&apos;t load your workspace.</p>
          <button
            type="button"
            className="mt-3 text-primary underline"
            onClick={() => setResolutionAttempt((attempt) => attempt + 1)}
          >
            Try again
          </button>
        </div>
      );
    }
    return <div className="p-8 text-sm text-muted-foreground">Loading workspace…</div>;
  }

  return <Navigate to={target} replace />;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <ThemeColorProvider>
        <TooltipProvider>
          <HelmetProvider>
            <Sonner />
            <BrowserRouter>
            <AuthProvider>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
              <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
              <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/welcome" element={<Welcome />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/about" element={<About />} />
              <Route path="/features" element={<Features />} />
              <Route path="/real-estate-crm" element={<RealEstateCrm />} />
              <Route path="/ai-crm" element={<AiCrm />} />
              <Route path="/crm-for-small-business" element={<SmallBusinessCrm />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/join/:inviteCode" element={<JoinGroup />} />
              <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
                <Route index element={<DashboardHomeRedirect />} />
                <Route path="profile" element={<Profile />} />
                <Route path="personal">
                  <Route index element={<Navigate to="/dashboard/personal/hot" replace />} />
                  <Route path="hot" element={<FreshLeads />} />
                  <Route path="cold" element={<ColdLeads />} />
                  <Route path="refresher" element={<Refresher />} />
                  <Route path="products" element={<Products />} />
                  <Route path="setup" element={<LeadSetup />} />
                </Route>
                <Route path="groups">
                  <Route index element={<DashboardHomeRedirect />} />
                  <Route path=":groupId" element={<GroupLayout />}>
                    <Route index element={<Navigate to="hot" replace />} />
                    <Route path="hot" element={<FreshLeads />} />
                    <Route path="cold" element={<ColdLeads />} />
                    <Route path="refresher" element={<Refresher />} />
                    <Route path="products" element={<Products />} />
                    <Route path="setup" element={<LeadSetup />} />
                  </Route>
                </Route>
              </Route>
              {/* Legacy /demo routes have been removed — redirect anyone with an old bookmark. */}
              <Route path="/demo/*" element={<Navigate to="/dashboard/personal/hot" replace />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            </AuthProvider>
          </BrowserRouter>
          </HelmetProvider>
        </TooltipProvider>
      </ThemeColorProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default Sentry.withErrorBoundary(App, {
  fallback: (
    <div className="min-h-screen flex items-center justify-center p-6 text-center">
      <div>
        <h1 className="font-display text-2xl mb-2">Something went wrong</h1>
        <p className="text-muted-foreground text-sm">The error has been reported. Please refresh the page.</p>
      </div>
    </div>
  ),
});
